import React from "react";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";

import routes from "../routes/routes";

// react components
import Button from "../components/Button";
import PageWrap from "../components/PageWrap";

// Redux actions
import { update as updateShowSpinner } from "../store/slices/showSpinner";
import { update as updateMode } from "../store/slices/mode";
import { update as updateManualOnly } from "../store/slices/manualOnly";
import { clear as clearError } from "../store/slices/error";
import { clearPictures } from "../store/slices/gallery";
import { reset as resetCountry } from "../store/slices/country";
import { reset as resetCurrentlyCapturing } from "../store/slices/currentlyCapturing";
import { reset as resetDocumentType } from "../store/slices/documentType";
import { reset as resetManualOnly } from "../store/slices/manualOnly";
import { reset as resetMode } from "../store/slices/mode";
import { reset as resetRetake } from "../store/slices/retake";
import { reset as resetSubject } from "../store/slices/subject";
import { update as updateSubmitted } from "../store/slices/submitted";

import api from "../api/api";

const Paragraph = styled.p`
  max-width: 310px;
  margin: 0 auto;
`;

const Heading = styled.h2`
  font-weight: bold;
  font-size: 1.125em;
  margin: 0 0 1.2em;
`;

const ExclamationIcon = styled.img`
  width: 78px;
  height: 78px;
  margin-bottom: 1em;
`;

const StyledLink = styled.a`
  color: ${(props) => props.theme.color.midBlue};
`;

export default function Error() {
  const history = useHistory();

  // Redux values
  const error = useSelector((state) => state.error.value);
  const token = useSelector((state) => state.token.value);
  const guid = useSelector((state) => state.guid.value);
  const scanUser = useSelector((state) => state.scanUser.value);

  const dispatch = useDispatch();

  /**
   * tells what to render for each type of error
   * plus defaults
   */
  const errorMessages = {
    default: {
      title: "Oops! Something went wrong.",
      description: (
        <React.Fragment>
          Please call{" "}
          <StyledLink href="tel:18667477432">1-866-747-SIEB (7432)</StyledLink>
          <br /> or email{" "}
          <StyledLink href="mailto:service@siebert.com">
            service@siebert.com
          </StyledLink>{" "}
          for assistance.
        </React.Fragment>
      ),
      action: "stop",
    },
    SDK_ERROR: {
      default: {
        title: "Unable to start camera",
        description:
          "Oops! We had trouble starting up your camera and would like to manually take a photo of your ID.",
        action: "manual capture only",
      },
      CONSTRAINT_NOT_SATISFIED: {
        title: "Camera resolution too low",
        description:
          "Oops! Looks like your camera resolution is too low for auto-capture. Let's try manually capturing your pictures.",
        action: "manual capture only",
      },
      NO_CAMERA_FOUND: {
        title: "Unable to find camera",
        description:
          "Oops! We couldn't find your camera. Please open this website on a mobile device with a working camera.",
        action: "stop",
      },
      CAMERA_PERMISSION_DENIED: {
        title: "No permission to use camera",
        description:
          "Oops! Looks like we don't have permission to use your camera. Please grant us permission to use your camera and use the arrow to go back or manually take a photo of your ID.",
        action: "manual capture only",
      },
    },
    TIMEOUT: {
      TIMEOUT: {
        title: "Hmmm.... That doesn't look right",
        description:
          "Oops!  Looks like there was something wrong with your image. We would like to manually take a photo of your ID.",
        action: "manual capture",
      },
    },
    api: {
      "expired id": {
        title: "Check Expiration Date",
        description:
          "Please confirm your id has not expired before continuing. Submitting an expired id will delay the creation of your account.",
        action: "offer restart",
      },
    },
  };

  /**
   * returns the title, description, and action to present to the user
   * depending on the type of error
   */
  const getErrorData = () => {
    if (errorMessages[error.event]) {
      if (errorMessages[error.event][error.type]) {
        return errorMessages[error.event][error.type];
      } else {
        return errorMessages[error.event]["default"];
      }
    }
    return errorMessages["default"];
  };

  /**
   * attempt to do manual capture
   */
  const manualCapture = () => {
    dispatch(updateMode("MANUAL_CAPTURE"));
    history.push(routes.instructions);
  };

  /**
   * attempt to do manual capture
   * set it so the user will only be offered manual capture going forward
   */
  const manualCaptureOnly = () => {
    dispatch(updateMode("MANUAL_CAPTURE"));
    dispatch(updateManualOnly(true));
    history.push(routes.instructions);
  };

  /**
   * offer to retry the whole process again
   * reset everything, send them back to the welcome screen
   */
  const restartCapture = () => {
    dispatch(updateSubmitted(false));
    dispatch(clearError());
    dispatch(clearPictures());
    dispatch(resetCountry());
    dispatch(resetCurrentlyCapturing());
    dispatch(resetDocumentType());
    dispatch(resetManualOnly());
    dispatch(resetMode());
    dispatch(resetRetake());
    dispatch(resetSubject());
    dispatch(updateShowSpinner(false));
    history.push(routes.welcome);
  };

  /**
   * signal that scan has stopped and go to the thank you screen
   */
  const submitId = () => {
    dispatch(updateSubmitted(true));
    api()
      .olaStopScan({ token: token, guid: guid, scanUser: scanUser })
      .then(() => {
        history.push(routes.thankYou);
      });
  };

  /**
   * render manual capture button or an empty div
   * if we're at a stopping point
   */
  const renderButton = () => {
    const action = getErrorData().action;

    if (action === "manual capture") {
      return (
        <Button type="button" onClick={manualCapture}>
          Manually Take Photos
        </Button>
      );
    }
    if (action === "manual capture only") {
      return (
        <Button type="button" onClick={manualCaptureOnly}>
          Manually Take Photos
        </Button>
      );
    }
    if (action === "offer restart") {
      return (
        <div style={{ marginTop: "1em" }}>
          <Button
            type="button"
            style={{ marginBottom: "0.5em" }}
            onClick={() => submitId()}
          >
            Submit ID
          </Button>
          <Button
            type="button"
            style={{ marginBottom: "0" }}
            onClick={restartCapture}
          >
            Start Over
          </Button>
        </div>
      );
    }
    return <div> </div>;
  };

  return (
    <PageWrap>
      <div>
        <ExclamationIcon src="/static/images/iconAlert.svg" alt="" />
        <Heading>{getErrorData().title}</Heading>
        <Paragraph>{getErrorData().description}</Paragraph>
      </div>
      {renderButton()}
    </PageWrap>
  );
}
